import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Divider,
  IconButton,
  alpha,
  useTheme,
  Chip,
  Stack,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComment } from "@fortawesome/free-solid-svg-icons";

const CommunicationNoteDialog = ({ open, onClose, note }) => {
  const theme = useTheme();

  if (!note) return null;

  const formatDateTime = (datetime) => {
    if (!datetime) return "N/A";
    try {
      const date = new Date(datetime);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch {
      return datetime.replace("T", " ");
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map(word => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const renderSignature = (signature) => {
    if (!signature) return null;

    if (signature.trim().startsWith('<svg')) {
      return (
        <Box
          sx={{
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            borderRadius: 2,
            p: 2,
            bgcolor: '#fafafa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 100,
            width: '100%',
            overflow: 'hidden'
          }}
          dangerouslySetInnerHTML={{ __html: signature }}
        />
      );
    }

    if (signature.startsWith('data:image')) {
      return (
        <Box
          sx={{
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            borderRadius: 2,
            p: 2,
            bgcolor: '#fafafa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 100
          }}
        >
          <Box
            component="img"
            src={signature}
            alt="Signature"
            sx={{ 
              maxHeight: 100, 
              maxWidth: '100%',
              objectFit: 'contain'
            }}
          />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          borderRadius: 2,
          p: 2,
          bgcolor: '#fafafa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 100
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}
        >
          <Typography variant="h5" fontWeight={700} color="primary">
            {getInitials(note.name)}
          </Typography>
        </Box>
      </Box>
    );
  };

  const getStatusChip = () => {
    if (note.private_note) {
      return {
        label: "Private",
        color: theme.palette.error.main
      };
    }
    return {
      label: "Public",
      color: theme.palette.success.main
    };
  };

  const status = getStatusChip();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          maxHeight: "90vh",
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FontAwesomeIcon
                icon={faComment}
                style={{ color: theme.palette.primary.main, fontSize: 20 }}
              />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} color="#1A1A2E">
                Communication Note Details
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="#6B7280">
                  {note.name || "Unknown"} • {note.position_name || "No Position"}
                </Typography>
                <Chip
                  label={status.label}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: "0.55rem",
                    fontWeight: 600,
                    bgcolor: alpha(status.color, 0.12),
                    color: status.color,
                  }}
                />
              </Stack>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        <Box display="flex" flexDirection="column" gap={2.5}>
          <Box>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              STAFF NAME
            </Typography>
            <Typography variant="body2" fontWeight={500} color="#1A1A2E">
              {note.name || "N/A"}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              POSITION / ROLE
            </Typography>
            <Typography variant="body2" fontWeight={500} color="#1A1A2E">
              {note.position_name || note.position || "N/A"}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              DATE & TIME
            </Typography>
            <Typography variant="body2" fontWeight={500} color="#1A1A2E">
              {formatDateTime(note.datetime)}
            </Typography>
          </Box>

          <Divider sx={{ opacity: 0.4 }} />

          <Box>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              REASON / SUBJECT
            </Typography>
            <Typography variant="body2" color="#475467" sx={{ whiteSpace: 'pre-wrap' }}>
              {note.reason || "N/A"}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              OUTCOME
            </Typography>
            <Typography variant="body2" color="#475467" sx={{ whiteSpace: 'pre-wrap' }}>
              {note.outcome || "N/A"}
            </Typography>
          </Box>

          {note.signature && (
            <Box>
              <Divider sx={{ mb: 2, opacity: 0.4 }} />
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                SIGNATURE
              </Typography>
              {renderSignature(note.signature)}
            </Box>
          )}

          {(note.handover || note.manager || note.family) && (
            <Box>
              <Divider sx={{ mb: 1.5, opacity: 0.4 }} />
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                SHARED WITH
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {note.handover && (
                  <Box sx={{ 
                    px: 1.5, 
                    py: 0.5, 
                    borderRadius: 1, 
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    fontSize: '0.7rem',
                    fontWeight: 600
                  }}>
                    Handover
                  </Box>
                )}
                {note.manager && (
                  <Box sx={{ 
                    px: 1.5, 
                    py: 0.5, 
                    borderRadius: 1, 
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                    fontSize: '0.7rem',
                    fontWeight: 600
                  }}>
                    Manager
                  </Box>
                )}
                {note.family && (
                  <Box sx={{ 
                    px: 1.5, 
                    py: 0.5, 
                    borderRadius: 1, 
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    color: theme.palette.warning.main,
                    fontSize: '0.7rem',
                    fontWeight: 600
                  }}>
                    Family
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        px: 2.5, 
        py: 2, 
        bgcolor: '#fafafa', 
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          sx={{ 
            px: 4, 
            fontWeight: 600, 
            textTransform: 'none',
            borderRadius: 2,
            bgcolor: '#0f172a',
            '&:hover': { bgcolor: '#1e293b' }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommunicationNoteDialog;